import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { getUserOrders } from '../../api/userApi';
import { FiStar, FiCheck, FiDownload, FiHeadphones, FiPackage, FiTruck, FiX, FiMap, FiExternalLink, FiChevronRight, FiChevronDown, FiInfo, FiSend } from 'react-icons/fi';
import { userApi } from '../../api/userApi';
import { isAuthenticated } from '../../api/api';

const ReviewModal = ({ order, onClose, onSubmit }) => {
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const productId = parseInt(order.productId);
    
    if (!productId || isNaN(productId)) {
      toast.error('Invalid product ID');
      return;
    }
    
    // Debug log to verify data
    console.log('Submitting review with data:', {
      productId,
      rating,
      review
    });

    onSubmit({ 
      productId,
      rating, 
      review 
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Product Info */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b">
          <img
            src={order.productImage}
            alt={order.productName}
            className="w-20 h-24 object-cover rounded"
          />
          <div>
            <h4 className="font-medium text-gray-900">{order.productName}</h4>
          </div>
        </div>

        <h3 className="text-lg font-semibold mb-4">Write Your Review</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Overall Rating
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                >
                  <FiStar
                    className={`w-6 h-6 ${
                      star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Review
            </label>
            <textarea
              value={review}
              onChange={(e) => setReview(e.target.value)}
              className="w-full border rounded-md px-3 py-2 h-32 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Share your experience with this product. What did you like or dislike?"
              required
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
            >
              Submit Review
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReturnOrderModal = ({ order, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Format the order number to match required format
      const orderNumber = order?.order_number;
      
      // Prepare data in the required format
      const returnData = {
        order_number: orderNumber,
        reason: reason.trim(),
        description: description.trim()
      };

      console.log('Submitting return request:', returnData);
      
      await onSubmit(returnData);
      onClose();
    } catch (error) {
      console.error('Return submission error:', error);
      toast.error(error.message || 'Failed to submit return request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl p-8 max-w-lg w-full"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h3 className="text-2xl font-light text-gray-800">Return Request</h3>
            <p className="text-sm text-gray-500 mt-1">Order #{order.order_number}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-2">Order Summary</h4>
          <p className="text-sm text-gray-600">Order Date: {new Date(order.created_at).toLocaleDateString()}</p>
          <p className="text-sm text-gray-600">Status: {order.status}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Reason Select */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Reason for Return <span className="text-red-500">*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-[#2C3E50] focus:ring focus:ring-[#2C3E50] focus:ring-opacity-20 transition-all"
            >
              <option value="">Select a reason</option>
              {RETURN_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-[#2C3E50] focus:ring focus:ring-[#2C3E50] focus:ring-opacity-20 transition-all"
              placeholder="Please describe the reason for your return..."
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FiSend className="w-4 h-4" />
                  <span>Submit Return Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [returnOrderData, setReturnOrderData] = useState(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const ordersData = await getUserOrders();
      
      if (!ordersData) {
        throw new Error('No orders data received');
      }
      
      setOrders(Array.isArray(ordersData) ? ordersData : ordersData.results || []);
    } catch (error) {
      console.error('Error in fetchOrders:', error);
      setError(error.message || 'Failed to fetch orders');
      toast.error(error.message || 'Failed to fetch orders', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    console.log('Order items data:', orders);
  }, [orders]);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState(null);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const handleDownloadInvoice = (order) => {
    if (order.invoice_url) {
      window.open(order.invoice_url, '_blank');
    } else {
      toast.error('Invoice not available');
    }
  };

  const openReturnModal = (order, item) => {
    if (!order?.order_number) {
      toast.error('Invalid order number');
      return;
    }
    if (!item?.id) {
      toast.error('Invalid item data');
      return;
    }
    
    setReturnOrderData({
      order: {
        order_number: order.order_number,
        selectedItem: {
          id: item.id
        }
      }
    });
  };

  const handleReturnSubmit = async (returnData) => {
    try {
      console.log('handleReturnSubmit - Return data:', returnData);

      if (!isAuthenticated()) {
        toast.error('Please log in to submit a return request');
        return;
      }

      if (!returnData.order_number) {
        throw new Error('Order number is required');
      }

      const formattedData = {
        order_number: returnData.order_number,
        reason: returnData.reason,
        description: returnData.description
      };

      await userApi.submitReturnRequest(formattedData);
      
      // Update the orders state to reflect the return request
      setOrders(prevOrders => prevOrders.map(order => {
        if (order.order_number === returnData.order_number) {
          return {
            ...order,
            items: order.items.map(item => ({
              ...item,
              return_requested: true
            }))
          };
        }
        return order;
      }));

      toast.success('Return request submitted successfully');
      setReturnOrderData(null);
      await fetchOrders(); // Refresh orders from the server
    } catch (error) {
      console.error('Error submitting return request:', error);
      toast.error(error.message || 'Failed to submit return request');
    }
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      // Ensure we have a valid product ID
      if (!reviewData.productId) {
        throw new Error('Product ID is required');
      }
      
      // No need to parse the ID again since we're getting it directly from variant_details
      await userApi.submitReview({
        productId: reviewData.productId,
        rating: reviewData.rating,
        review: reviewData.review
      });
      
      toast.success('Review submitted successfully');
      setShowReviewModal(false);
      setSelectedOrderForReview(null);
      await fetchOrders(); // Refresh orders list
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.message || 'Failed to submit review');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
    </div>;
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl font-light text-gray-900 mb-2">My Orders</h1>
          <div className="w-20 h-1 bg-[#2C3E50]"></div>
        </motion.div>

        {/* Orders List */}
        <div className="space-y-8">
          <AnimatePresence>
            {orders.map((order) => (
              <motion.div
                key={order.id || order._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                {/* Order Items */}
                <div className="divide-y divide-gray-50">
                  {(order.items || []).map((item) => (
                    <div key={item.id || item._id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                      <div className="flex gap-6">
                        {/* Product Image */}
                        <div className="relative group w-24 h-32 overflow-hidden bg-gray-100">
                          <img
                            src={item.variant_details?.image || '/placeholder.jpg'}
                            alt={item.variant_details?.product_name}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        
                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          {/* Product Name and Price */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                            <h3 className="text-lg font-medium text-gray-900 hover:text-[#2C3E50] transition-colors">
                              {item.variant_details?.product_name}
                            </h3>
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-medium text-gray-900">
                                ₹{parseFloat(item.price).toLocaleString('en-IN')}
                              </span>
                            </div>
                          </div>

                          {/* All Details Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Order Date</p>
                              <p className="text-sm text-gray-700">
                                {new Date(order.created_at).toLocaleDateString('en-US', { 
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Order ID</p>
                              <p className="text-sm font-mono text-gray-700">{order.order_number}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Amount</p>
                              <p className="text-sm text-gray-700">₹{parseFloat(order.total_amount).toLocaleString('en-IN')}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
                              <span className={`inline-flex px-3 py-1 text-xs font-medium ${getStatusColor(order.status)}`}>
                                {order.status.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Brand</p>
                              <p className="text-sm text-gray-700">{item.variant_details?.brand?.name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Category</p>
                              <p className="text-sm text-gray-700">{item.variant_details?.category?.name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Quantity</p>
                              <p className="text-sm text-gray-700">{item.quantity}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Size</p>
                              <p className="text-sm text-gray-700">{item.variant_details?.size || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Color</p>
                              <p className="text-sm text-gray-700">{item.variant_details?.color || 'N/A'}</p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap items-center gap-3">
                            {order.status === 'completed' && !item.hasReview && (
                              <button
                                onClick={() => {
                                  // Add console.log to see what data we have
                                  console.log('Item data:', item);
                                  
                                  const productId = parseInt(
                                    item.product_id || 
                                    item.productId || 
                                    item.variant_details?.product_id || 
                                    item.id
                                  );

                                  if (!productId || isNaN(productId)) {
                                    toast.error('Invalid product ID');
                                    return;
                                  }
                                  
                                  const orderData = {
                                    orderId: order.id,
                                    productId: productId,
                                    productName: item.variant_details?.product_name || 
                                                item.product_name || 
                                                item.name,
                                    productImage: item.variant_details?.image || 
                                                 item.product_image || 
                                                 item.image
                                  };
                                  
                                  console.log('Setting review data:', orderData); // Debug log
                                  
                                  setSelectedOrderForReview(orderData);
                                  setShowReviewModal(true);
                                }}
                                className="inline-flex items-center px-4 py-2 border border-[#2C3E50] text-[#2C3E50] text-sm font-medium hover:bg-[#2C3E50] hover:text-white transition-all duration-300"
                              >
                                <FiStar className="w-4 h-4 mr-2" />
                                Write Review
                              </button>
                            )}
                            
                            {/* Add Return Button for each product */}
                            {order.status.toLowerCase() === 'completed' && (
                              <button
                                onClick={() => openReturnModal(order, item)}
                                disabled={item.return_requested || item.isReturned}
                                className={`inline-flex items-center px-4 py-2 border ${
                                  item.return_requested || item.isReturned
                                    ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100'
                                    : 'border-gray-500 text-gray-700 hover:bg-gray-700 hover:text-white'
                                } text-sm font-medium transition-all duration-300`}
                              >
                                <FiPackage className="w-4 h-4 mr-2" />
                                {item.return_requested ? 'Return Requested' : item.isReturned ? 'Returned' : 'Return Item'}
                              </button>
                            )}
                            
                            {item.hasReview && (
                              <div className="inline-flex items-center text-green-600 text-sm">
                                <FiCheck className="w-4 h-4 mr-2" />
                                Reviewed
                              </div>
                            )}
                            
                            {item.isReturned && (
                              <div className="inline-flex items-center text-gray-600 text-sm">
                                <FiPackage className="w-4 h-4 mr-2" />
                                Return Requested
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Footer */}
                <div className="bg-[#FAFBFC] px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-6">
                    {['shipped', 'completed'].includes(order.status.toLowerCase()) && (
                      <button
                        onClick={() => handleDownloadInvoice(order)}
                        className="inline-flex items-center text-sm text-gray-600 hover:text-[#2C3E50] transition-colors"
                      >
                        <FiDownload className="w-4 h-4 mr-2" />
                        Download Invoice
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {order.status.toLowerCase() === 'shipped' && order.tracking_link && (
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="px-4 py-2 bg-[#2C3E50] text-white text-sm font-medium hover:bg-[#34495E] transition-all duration-300 flex items-center"
                      >
                        <FiMap className="w-4 h-4 mr-2" />
                        View Tracking Details
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Tracking Modal */}
        {selectedOrder && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="text-2xl font-light">Shipment Tracking</h3>
                  <p className="text-sm text-gray-500">Order #{selectedOrder.order_number}</p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Order Date</p>
                      <p className="font-medium">
                        {new Date(selectedOrder.created_at).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="font-medium capitalize">{selectedOrder.status}</p>
                    </div>
                  </div>
                </div>

                {/* Tracking Link */}
                {selectedOrder.status.toLowerCase() === 'shipped' && selectedOrder.tracking_link && (
                  <div className="border rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <FiTruck className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Courier Tracking Available</h4>
                          <p className="text-sm text-gray-500">Track your package on courier's website</p>
                        </div>
                      </div>
                      <a
                        href={selectedOrder.tracking_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                      >
                        Track Package
                        <FiExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Review Modal */}
        {showReviewModal && selectedOrderForReview && (
          <ReviewModal
            order={selectedOrderForReview}  // Passing the selected order data
            onClose={() => {
              setShowReviewModal(false);
              setSelectedOrderForReview(null);
            }}
            onSubmit={handleReviewSubmit}
          />
        )}
        {returnOrderData && (
          <ReturnOrderModal 
            order={returnOrderData.order}
            selectedItem={returnOrderData.selectedItem}
            onClose={() => setReturnOrderData(null)}
            onSubmit={handleReturnSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default Orders;

const RETURN_REASONS = [
  { value: 'defective', label: 'Product is defective' },
  { value: 'wrong_item', label: 'Received wrong item' },
  { value: 'not_as_described', label: 'Item not as described' },
  { value: 'other', label: 'Other reason' }
];







